import {
  ADMIN_ROLES,
  ADVISOR_ROLES,
  CATALOGUE_READ_ROLES,
  CATALOGUE_ROLES,
  holdsAny,
  INTERNAL_ROLES,
} from '@brickup/shared/constants'
import { UserRole } from '@brickup/shared/enums'
import { ACCOUNT } from '@brickup/shared/locales/de/account'
import { NAV } from '@brickup/shared/locales/de/nav'
import { SYSTEM_GROUPS } from '@/constants/system-navigation'
import { longestMatch } from '@/utils/route-path'

// The groups come from `shared/constants/user-roles.ts`, where the API reads
// them too, so an area cannot be offered here and refused there. SALES
// ("Kundendienst") sees the same business areas as ADMIN but has NO access to
// `/internal/system/*` — the entire settings surface — and the architect
// reaches neither matching nor the objects it works on.

/** An area outside System, with the roles that may open it and the name the
 *  permissions page lists it under. */
export type NavArea = {
  label: string
  roles: readonly UserRole[]
  /** Who may change what is on it. Defaults to whoever may open it — a
   *  business area is worked in by everyone who reaches it. */
  writeRoles?: readonly UserRole[]
  /**
   * True where the screens under it answer the question themselves, so the
   * area's own list is the widest of several rules. The permissions page says
   * so rather than stating the widest as if it were the rule.
   */
  changeVariesByScreen?: boolean
}

export const AREAS: Record<string, NavArea> = {
  '/internal/dashboard': { label: NAV.dashboard, roles: INTERNAL_ROLES },
  // Every internal role works here: an architect writes a project's notes and
  // files documents on it. What the project *is* — its status, its customer,
  // what it costs — is narrower and needs the record, so the screens that
  // change it set their own scope from `useCanChangeProject`.
  '/internal/projects': {
    label: NAV.projects,
    roles: INTERNAL_ROLES,
    changeVariesByScreen: true,
  },
  // Read by more roles than change it: a project manager and a site manager
  // look up what a project is built from. The screens disable their controls
  // from the same rule, and `catalogue-route-roles.spec.ts` holds the API to
  // it per route.
  '/internal/catalogue': {
    label: NAV.catalogue,
    roles: CATALOGUE_READ_ROLES,
    writeRoles: CATALOGUE_ROLES,
  },
  '/internal/media': { label: NAV.mediaLibrary, roles: CATALOGUE_ROLES },
  '/internal/properties': { label: NAV.properties, roles: ADVISOR_ROLES },
  '/internal/matching': { label: NAV.matching, roles: ADVISOR_ROLES },
  // Not in the rail, but every signed-in staff role reaches it from the user
  // menu — an unlisted route is a denied one, so it has to stand here.
  '/internal/account': {
    label: ACCOUNT.accountSettings,
    roles: INTERNAL_ROLES,
  },
  // The advisor's "as customer" view. Not in the rail either — it is entered
  // from a customer's row. Worked in, not only watched: the advisor edits the
  // customer's own configuration from inside it, through the routes
  // `CustomerShadowController` serves.
  '/internal/customer-view': { label: NAV.customerView, roles: ADVISOR_ROLES },
  // The prefix every System page falls back to. What may be changed under one
  // is the group's own answer, read by the layout there.
  '/internal/system': { label: NAV.system, roles: ADMIN_ROLES },
  '/customer/dashboard': {
    label: NAV.customerArea,
    roles: [UserRole.CUSTOMER, UserRole.ROOT],
  },
}

/**
 * Every route, with the roles that may open it.
 *
 * The System half is generated from `SYSTEM_GROUPS`: an unlisted path falls
 * back to the longest prefix above it, so an area written here by hand and
 * then forgotten would be granted to whoever may open `/internal/system` — a
 * silent widening on the one table where that matters. `basePath` covers the
 * segment a group's index redirect occupies before any area answers for it.
 */
const NAV_PERMISSIONS: Record<string, readonly UserRole[]> = {
  ...Object.fromEntries(
    Object.entries(AREAS).map(([path, area]) => [path, area.roles] as const),
  ),
  ...Object.fromEntries(
    SYSTEM_GROUPS.flatMap(group => [
      ...(group.basePath ? [[group.basePath, group.roles] as const] : []),
      // Intersected, not substituted: `SystemArea.roles` is documented as a
      // narrowing, and an area handed a wider list would otherwise grant the
      // route while the rail still hid it — reachable and unlisted.
      ...group.areas.map(
        area =>
          [
            String(area.href),
            area.roles
              ? area.roles.filter(role => group.roles.includes(role))
              : group.roles,
          ] as const,
      ),
    ]),
  ),
}

const ROUTES = Object.keys(NAV_PERMISSIONS)
const AREA_PATHS = Object.keys(AREAS)

/**
 * The roles that may open a path, by the longest route in the table.
 *
 * `/internal/system/operations/email/subscriptions` is not a route of its own — the page
 * holding it is — and a lookup on the exact path alone would read that as
 * "nobody", hiding a tab from the role that may open it.
 */
export function allowedRolesFor(
  pathname: string,
): readonly UserRole[] | undefined {
  const match = longestMatch(pathname, ROUTES)

  return match ? NAV_PERMISSIONS[match] : undefined
}

/** Whether any of these roles may open a path — the question the rail, the
 *  System groups and the route guard all ask. */
export const mayOpen = (
  roles: readonly UserRole[] | null | undefined,
  pathname: string,
): boolean => holdsAny(roles, allowedRolesFor(pathname) ?? [])

/**
 * The roles that may change what is on a path, by the same longest match.
 *
 * An area states it once and `InternalLayout` puts the screen in a read-only
 * scope for everyone else — so a role that reads an area without working in it
 * is refused by the one table the permissions page shows, rather than by each
 * screen remembering to ask. A System page narrows it again from its group.
 */
export function writeRolesFor(
  pathname: string,
): readonly UserRole[] | undefined {
  const match = longestMatch(pathname, AREA_PATHS)

  if (!match) return undefined
  return AREAS[match].writeRoles ?? AREAS[match].roles
}
